import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save, FolderOpen, Hash, Palette } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { LoadingButton } from '../components/ui/LoadingButton'
import { Input } from '../components/ui/Input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { apiClient } from '../services/api'
import toast from 'react-hot-toast'

interface CategoryFormData {
  name: string
  description: string
  sort_order: string
  color: string
  is_active: boolean
}

const colorOptions = [
  { value: '#ef4444', label: 'Red', name: 'red' },
  { value: '#f97316', label: 'Orange', name: 'orange' },
  { value: '#eab308', label: 'Yellow', name: 'yellow' },
  { value: '#22c55e', label: 'Green', name: 'green' },
  { value: '#3b82f6', label: 'Blue', name: 'blue' },
  { value: '#8b5cf6', label: 'Purple', name: 'purple' },
  { value: '#ec4899', label: 'Pink', name: 'pink' },
  { value: '#6b7280', label: 'Gray', name: 'gray' }
]

export const CategoryForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditMode = Boolean(id)

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    description: '',
    sort_order: '0',
    color: '#f97316',
    is_active: true
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (isEditMode && id) {
      loadCategory(id)
    }
  }, [isEditMode, id])

  const loadCategory = async (categoryId: string) => {
    try {
      setLoading(true)
      const response = await apiClient.getCategory(categoryId)
      const category = response.category
      
      setFormData({
        name: category.name || '',
        description: category.description || '',
        sort_order: category.sort_order?.toString() || '0',
        color: category.color || '#f97316',
        is_active: category.is_active !== false
      })
    } catch (error) {
      console.error('Failed to load category:', error)
      toast.error('Failed to load category')
      navigate('/categories')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: keyof CategoryFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required'
    }

    if (formData.name.trim().length < 2) {
      newErrors.name = 'Category name must be at least 2 characters long'
    }

    const sortOrder = parseInt(formData.sort_order)
    if (isNaN(sortOrder) || sortOrder < 0) {
      newErrors.sort_order = 'Sort order must be a valid positive number'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      toast.error('Please fix the validation errors')
      return
    }

    setSaving(true)
    
    try {
      const categoryData = {
        ...formData,
        sort_order: parseInt(formData.sort_order)
      }

      if (isEditMode && id) {
        await apiClient.updateCategory(id, categoryData)
        toast.success('Category updated successfully!')
      } else {
        await apiClient.createCategory(categoryData)
        toast.success('Category created successfully!')
      }

      navigate('/categories')
    } catch (error: any) {
      toast.error(error.message || `Failed to ${isEditMode ? 'update' : 'create'} category`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading category...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/categories')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Categories
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {isEditMode ? 'Edit Category' : 'Add New Category'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {isEditMode ? 'Update category information' : 'Create a new product category'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5" />
              Category Information
            </CardTitle>
            <CardDescription>
              Enter the basic details about your product category
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Category Name *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter category name (e.g., Electronics, Clothing)"
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && (
                <p className="text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                placeholder="Enter category description"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-700"
              />
              <p className="text-xs text-gray-500">
                Optional description to help identify products in this category
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Display Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Display Settings
            </CardTitle>
            <CardDescription>
              Customize how this category appears in your system
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  Sort Order
                </label>
                <Input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => handleInputChange('sort_order', e.target.value)}
                  placeholder="0"
                  className={errors.sort_order ? 'border-red-500' : ''}
                />
                {errors.sort_order && (
                  <p className="text-sm text-red-600">{errors.sort_order}</p>
                )}
                <p className="text-xs text-gray-500">
                  Lower numbers appear first in category lists
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Category Color
                </label>
                <div className="flex items-center gap-2">
                  <div
                    className="w-10 h-10 rounded-lg border-2 border-gray-300"
                    style={{ backgroundColor: formData.color }}
                  ></div>
                  <select
                    value={formData.color}
                    onChange={(e) => handleInputChange('color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-700"
                  >
                    {colorOptions.map(color => (
                      <option key={color.value} value={color.value}>
                        {color.label}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-gray-500">
                  Color used for visual identification in the POS system
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Active Category
                </span>
              </label>
              <Badge variant={formData.is_active ? 'success' : 'secondary'}>
                {formData.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card>
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>
              See how this category will appear in your system
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-semibold"
                style={{ backgroundColor: formData.color }}
              >
                {formData.name ? formData.name.charAt(0).toUpperCase() : '?'}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  {formData.name || 'Category Name'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {formData.description || 'Category description will appear here'}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-500">Sort: {formData.sort_order}</span>
                  <Badge variant={formData.is_active ? 'success' : 'secondary'} size="sm">
                    {formData.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex items-center gap-4 pt-6">
          <LoadingButton
            type="submit"
            isLoading={saving}
            loadingText={isEditMode ? "Updating..." : "Creating..."}
            className="flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {isEditMode ? 'Update Category' : 'Create Category'}
          </LoadingButton>
          
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/categories')}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  )
}